import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { createSet } from "../../actions/setsActions";
import { formatDuration, formatSeconds } from "../../lib/clipboard";

/** What the host sees after ending a session: the numbers, and a way to keep the set. */
const EndedPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const summary = useSelector((state) => state.summary);
  const hasKey = useSelector((state) => state.host.hasKey);
  const [saving, setSaving] = useState(false);

  if (!summary) return <Navigate to="/" replace />;

  const people = summary.PeakListeners || 0;
  const songs = summary.SongsQueued ?? summary.SongIds?.length ?? 0;
  const held = summary.HeldLongest;

  // The summary stays in the store until another session starts (clearing it here would
  // make this page redirect home while the router is still on its way to the next page).
  const leave = (to) => navigate(to, { replace: true });

  const done = () => leave("/");

  const save = async () => {
    const name = window.prompt("Name for the set", summary.SetName || "");
    if (!name || !name.trim()) return;
    setSaving(true);
    const set = await dispatch(createSet({ name: name.trim(), songIds: summary.SongIds || [] }));
    setSaving(false);
    if (set) leave("/sets");
  };

  return (
    <div className="page" data-screen-label="Ended">
      <div className="check-badge" aria-hidden="true">
        ✓
      </div>
      <h2 className="page-title">That was {summary.SetName || `session ${summary.Code}`}</h2>
      <p className="page-sub" role="status">
        {summary.pending ? "Wrapping up the session…" : formatDuration(summary.DurationSeconds)}
        {!summary.pending && people > 0 && ` · ${people} ${people === 1 ? "person" : "people"} sang along`}
      </p>
      <div className="stat-grid">
        <div className="stat">
          <div className="stat-value">{songs}</div>
          <div className="stat-label">{songs === 1 ? "song" : "songs"}</div>
        </div>
        <div className="stat stat-accent">
          <div className="stat-value">{summary.Reactions || 0}</div>
          <div className="stat-label">hearts</div>
        </div>
      </div>
      {held && (
        <div className="card">
          <div className="kicker" style={{ marginBottom: 10 }}>
            Held longest
          </div>
          <div className="row-title">
            {held.SongTitle} — {held.Section}
          </div>
          <div className="row-sub">{formatSeconds(held.Seconds)} on screens</div>
        </div>
      )}
      {summary.UnplayedRequests > 0 && (
        <div className="card-dashed">
          <div className="card-title">
            {summary.UnplayedRequests} {summary.UnplayedRequests === 1 ? "request" : "requests"} went unplayed
          </div>
          <div className="row-sub">They are gone with the session; the songs are still in the lists.</div>
        </div>
      )}
      <div style={{ flex: 1 }} />
      {hasKey && summary.SongIds?.length > 0 && (
        <button type="button" className="btn btn-md btn-primary btn-block" onClick={save} disabled={saving || summary.pending}>
          {saving ? "Saving…" : "Save as a set"}
        </button>
      )}
      <button type="button" className="btn btn-md btn-outline btn-block" onClick={done}>
        Done
      </button>
    </div>
  );
};

export default EndedPage;
